import { Injectable, EventEmitter } from '@angular/core';
import { IStep } from '@models/step-container.model';
import { AppConfiguration } from '@config/app-configuration';

@Injectable({ providedIn: 'root' })
export class StepperService {

  public steps: IStep[] = [];
  public currentStep: IStep;
  public stepChanged: EventEmitter<IStep> = new EventEmitter<IStep>();

  public mode: 'linear' |'conditional' = 'conditional';
  public ignoreExceptions = true;
  
  private historyEnabled = false;
  private isNavigatingViaHistory = false;
  private boundHandleBrowserBack: ((event: PopStateEvent) => void) | null = null;
  private maxStepReached = 1; // Track the maximum step user has reached

  constructor(private appConfig: AppConfiguration) { }

  public setCurrentStep(step: IStep) {
    this.updateCurrentStep(step);
  }

  public setCurrentStepNo(stepNo: number) {
    this.findAndUpdateStep(stepNo);
  }
  
  public enableBrowserHistory() {
    if (this.historyEnabled) {
      return; // Already enabled, prevent duplicate listeners
    }
    
    this.historyEnabled = true;
    this.boundHandleBrowserBack = this.handleBrowserBack.bind(this);
    window.addEventListener('popstate', this.boundHandleBrowserBack);
  }
  
  public disableBrowserHistory() {
    if (!this.historyEnabled) {
      return; // Already disabled
    }
    
    this.historyEnabled = false;
    if (this.boundHandleBrowserBack) {
      window.removeEventListener('popstate', this.boundHandleBrowserBack);
      this.boundHandleBrowserBack = null;
    }
  }
  
  private handleBrowserBack(event: PopStateEvent) {
    const targetStepNumber = event.state?.step;  // Step number user is navigating TO (via browser btn)
    const fromStepNumber = this.currentStep?.step;  // Step number user is navigating FROM
    
    // If already navigating via history, skip to avoid recursive loops
    if (this.isNavigatingViaHistory) {
      return;
    }
    
    // Block browser forward navigation - only allow backward navigation
    if (targetStepNumber && typeof targetStepNumber === 'number' && fromStepNumber && targetStepNumber > fromStepNumber) {
      // Go back to cancel the forward navigation
      this.isNavigatingViaHistory = true;
      history.back();
      setTimeout(() => {
        this.isNavigatingViaHistory = false;
      }, 50);
      return;
    }
    
    if (targetStepNumber === 0 || (event.state && event.state.isBase)) {
     
      if (fromStepNumber && fromStepNumber > 1) {
        this.isNavigatingViaHistory = true;
        this.setCurrentStepNo(1);
        history.replaceState({ step: 1 }, '', window.location.pathname);
        setTimeout(() => {
          this.isNavigatingViaHistory = false;
        }, 100);
      }
      return;
    }

    if (targetStepNumber && typeof targetStepNumber === 'number' && targetStepNumber >= 1) {
      const stepExists = this.steps?.find(s => s.step === targetStepNumber);
    
      if (stepExists) {
        this.isNavigatingViaHistory = true;
        this.setCurrentStepNo(targetStepNumber);
        setTimeout(() => {
          this.isNavigatingViaHistory = false;
        }, 100);
      }
    } else if (targetStepNumber === undefined || targetStepNumber === null) {
     
      if (fromStepNumber && fromStepNumber > 1) {
        this.isNavigatingViaHistory = true;
        this.setCurrentStepNo(1);
        history.replaceState({ step: 1 }, '', window.location.pathname);
        setTimeout(() => {
          this.isNavigatingViaHistory = false;
        }, 100);
      }
    }
  }

  public markStepCompleted(nextStep: number) {
    const nextStepNumber = this.mode === 'conditional' ? nextStep : this.currentStep.step + 1;
    this.findAndUpdateStep(nextStepNumber);
  }

  public markStepFailed(error: any, nextStep?: number, forceProceedOnIgnore?: number) {
    console.log(error);
    if (this.ignoreExceptions) {
      this.findAndUpdateStep(forceProceedOnIgnore);
    } else {
      const nextStepNumber = this.mode === 'conditional' ? nextStep : this.currentStep.step + 1;
      this.findAndUpdateStep(nextStepNumber);
    }
  }

  private findAndUpdateStep(nextStepNumber: number) {
    const filteredNextStep = this.steps.filter(f => f.step === nextStepNumber);
    if (filteredNextStep && filteredNextStep.length > 0) {
      const filteredStep = filteredNextStep[0];
      filteredStep.canActivate = true;
      this.updateCurrentStep(filteredStep);
    }
  }

  private updateCurrentStep(filteredStep: IStep) {
    if (!this.historyEnabled) {
      this.currentStep = filteredStep;
      this.stepChanged.emit(filteredStep);
      return;
    }

    const previousStepNumber = this.currentStep?.step;
    const currentStepNumber = filteredStep.step;
    
  
    
    if (previousStepNumber === currentStepNumber) {
    
      return;
    }
    
    this.currentStep = filteredStep;
    this.stepChanged.emit(filteredStep);
    
    // Track the maximum step reached when moving forward via UI
    if (currentStepNumber > this.maxStepReached) {
      this.maxStepReached = currentStepNumber;
     
    }

    if (this.isNavigatingViaHistory) {
    
      return;
    }

    if (!previousStepNumber || previousStepNumber < currentStepNumber) {
      // Moving forward or initial step - push new state
      history.pushState({ step: currentStepNumber }, '', window.location.pathname);
    } else if (previousStepNumber > currentStepNumber) {
      // Moving backward - use history.go() to navigate back
      const stepsToGoBack = previousStepNumber - currentStepNumber;
      this.isNavigatingViaHistory = true;
      history.go(-stepsToGoBack);
      setTimeout(() => {
        this.isNavigatingViaHistory = false;
      }, 200);
    }
  }

  public disbaleActivateFurtherSteps(step: IStep) {
    const disbableFutureSteps = this.steps.filter(f => f.step > step.step && !f.hide);
    if (disbableFutureSteps && disbableFutureSteps.length > 0) {
      disbableFutureSteps.forEach((s) => {
        s.canActivate = false;
      });
    }
  }

  public disbaleActivateFurtherStepsAfterThirdStep(step: IStep) {
    const disbableFutureSteps = this.steps.filter(f => f.step < step.step);
    if (disbableFutureSteps && disbableFutureSteps.length > 0) {
      disbableFutureSteps.forEach((s) => {
        s.canActivate = false;
      });
    }
  }
}
